import { useState, useCallback, useEffect } from "react";

const STORAGE_KEY = "trades-toolbelt-recent-tools";
const MAX_RECENT = 6;

export interface RecentTool {
  path: string;
  label: string;
  visitCount: number;
  lastVisited: number; // timestamp
}

const load = (): RecentTool[] => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
  } catch {
    return [];
  }
};

const save = (tools: RecentTool[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tools));
  } catch {}
};

export const useRecentTools = () => {
  const [recentTools, setRecentTools] = useState<RecentTool[]>(load);

  // Sync across tabs
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setRecentTools(load());
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const trackVisit = useCallback((path: string, label: string) => {
    // Don't track home page
    if (path === "/" || path === "/install") return;

    setRecentTools(prev => {
      const existing = prev.find(t => t.path === path);
      let updated: RecentTool[];
      if (existing) {
        updated = prev.map(t =>
          t.path === path
            ? { ...t, label, visitCount: t.visitCount + 1, lastVisited: Date.now() }
            : t
        );
      } else {
        updated = [...prev, { path, label, visitCount: 1, lastVisited: Date.now() }];
      }
      // Keep top N by visit count (tie-break by recency)
      updated.sort((a, b) => b.visitCount - a.visitCount || b.lastVisited - a.lastVisited);
      updated = updated.slice(0, MAX_RECENT);
      save(updated);
      return updated;
    });
  }, []);

  const clearRecent = useCallback(() => {
    setRecentTools([]);
    save([]);
  }, []);

  // Sorted: most visited first, then most recent
  const sorted = [...recentTools].sort(
    (a, b) => b.visitCount - a.visitCount || b.lastVisited - a.lastVisited
  );

  return { recentTools: sorted, trackVisit, clearRecent };
};
